import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation, useNavigationType } from "react-router-dom";

import type { TrayFocus } from "@/lib/kory-feed";
import type { AskAboutEntity } from "@/lib/kory-palette";
import {
  driveSubjectId,
  resolveKoryPageSubject,
  routeNeedsWidth,
  trayShows,
  type KoryPageSubject,
} from "@/lib/kory-page-subject";

/** A ⌘K handoff: the words to send in the tray's one conversation (null: just open it), and the
 *  results the palette was showing. ``nonce`` tells one handoff from the next. */
/** Handoffs kept for a tray that hasn't sent them yet. */
const PALETTE_ASKS_KEPT = 5;

/** The tray's focus as the tray resolved it: the item open in the tray (its outline). Kory pointing at
 *  it is a separate mark, the gold leaf (``kory-focus``). */
export type SharedTrayFocus = TrayFocus;
/** The tray's current presentation. Page layouts use this rather than inferring it from the DOM. */
export type KoryTrayMode = "folded" | "docked" | "floating" | "phone-sheet";

/** Door 1 from the page ("Work on this" on a feed card); ``at`` orders it against a raise. */
export type TrayPick = { key: string; at: Date };
/** The Drive the tray talks to: the one this page is about, or the one pinned across pages. */
export interface DriveFocus {
  id: string;
  label: string;
  /** Held by a pin, not by the page: it stays as you move between pages. */
  pinned: boolean;
}

export interface PaletteAsk {
  nonce: number;
  text: string | null;
  results: string | null;
}

/**
 * The global Kory tray's shell state: which page subject is declared, whether
 * the tray is showing (the per-user flag and the page's mode), and an
 * ``openTray`` the ``K`` key, cell capture and the meeting rail call. The
 * tray itself registers ``onOpen`` and is the only thing that ever raises it.
 */
interface KoryTrayContextType {
  subject: KoryPageSubject;
  /** The tray is mounted and showing on this page. */
  active: boolean;
  /** The per-user flag: the global tray is on for this person (on any page that has one). */
  enabled: boolean;
  /** The tray's current presentation, reported by the tray as it changes. */
  trayMode: KoryTrayMode;
  setTrayMode: (mode: KoryTrayMode) => void;
  /** The width (px) this route needs beside a docked tray; short of it the tray floats over the page. */
  needsWidth: number;
  /** A host page (Overview) hands its rows to the global tray instead of mounting its own: the
   *  element the tray renders where its rows go, and the page's claim on it. */
  briefSlot: HTMLElement | null;
  setBriefSlot: (el: HTMLElement | null) => void;
  briefClaimed: boolean;
  claimBrief: (key: string, on: boolean) => void;
  /** A host page that mounted its own tray (Overview without the session): the global one steps aside. */
  claimOwnTray: (key: string, on: boolean) => void;
  openTray: () => void;
  /** Bumped by ``openTray``; the tray rises and focuses its composer on each bump. */
  openNonce: number;
  /** A folded tray rises to show its focus ("Work on this"); the composer keeps out of it. */
  raiseTray: () => void;
  /** Bumped by ``raiseTray``. */
  raiseNonce: number;
  setEnabled: (enabled: boolean) => void;
  declare: (key: string, subject: KoryPageSubject | null) => void;
  /** The result last picked in ⌘K: the tray's "Ask about" chip, until the page after it. */
  askAbout: AskAboutEntity | null;
  /** ``navigating``: the pick is on its way to ``entity.url``; the chip lasts while that page shows. */
  setAskAbout: (entity: AskAboutEntity | null, opts?: { navigating?: boolean }) => void;
  /** ⌘K handoffs in order (the last few); the tray rises and sends each, as the same conversation. */
  paletteAsks: PaletteAsk[];
  askInTray: (ask: { text: string | null; results: string | null }) => void;
  /** The tray's focus (focus mode), published by the tray: the feed outlines it. */
  trayFocus: SharedTrayFocus | null;
  setTrayFocus: (focus: SharedTrayFocus | null) => void;
  /** The last item picked from the page; the tray makes it its focus. */
  picked: TrayPick | null;
  pick: (key: string) => void;
  /** The Drive the tray talks to, if any. A Drive page focuses its own; a pin keeps one across pages. */
  driveFocus: DriveFocus | null;
  pinDrive: (drive: { id: string; label: string }) => void;
  unpinDrive: () => void;
}

const KoryTrayContext = createContext<KoryTrayContextType | undefined>(undefined);

function toggleClaim(prev: string[], key: string, on: boolean): string[] {
  if (on) return prev.includes(key) ? prev : [...prev, key];
  return prev.filter((k) => k !== key);
}

export function KoryTrayProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  // Declarations in mount order; the latest (the innermost page) wins.
  const [declared, setDeclared] = useState<{ key: string; subject: KoryPageSubject }[]>([]);
  const [enabled, setEnabled] = useState(false);
  const [trayMode, setTrayMode] = useState<KoryTrayMode>("folded");
  const [openNonce, setOpenNonce] = useState(0);

  const declare = useCallback((key: string, subject: KoryPageSubject | null) => {
    setDeclared((prev) => {
      const rest = prev.filter((d) => d.key !== key);
      return subject ? [...rest, { key, subject }] : rest;
    });
  }, []);

  const [briefSlot, setBriefSlot] = useState<HTMLElement | null>(null);
  const [briefClaims, setBriefClaims] = useState<string[]>([]);
  const claimBrief = useCallback((key: string, on: boolean) => setBriefClaims((prev) => toggleClaim(prev, key, on)), []);
  const [ownTrayClaims, setOwnTrayClaims] = useState<string[]>([]);
  const claimOwnTray = useCallback((key: string, on: boolean) => setOwnTrayClaims((prev) => toggleClaim(prev, key, on)), []);

  const subject = resolveKoryPageSubject(pathname, declared.at(-1)?.subject ?? null);
  const briefClaimed = briefClaims.length > 0;
  // A host page shows the one global tray (its rows handed over, or still loading), so the tray never
  // remounts between pages. Only a host that mounted a tray of its own hides it.
  const active = enabled && (trayShows(subject) || (subject.mode === "host" && ownTrayClaims.length === 0));
  const needsWidth = routeNeedsWidth(pathname);
  const openTray = useCallback(() => setOpenNonce((n) => n + 1), []);
  const [raiseNonce, setRaiseNonce] = useState(0);
  const raiseTray = useCallback(() => setRaiseNonce((n) => n + 1), []);

  // The chip holds through the navigation its pick started, and goes with the next one.
  // ``following``: just arrived, so a redirect from here (a replace in the same turn) carries the chip on.
  const [about, setAbout] = useState<{
    entity: AskAboutEntity;
    path: string;
    arrived: boolean;
    following: boolean;
  } | null>(null);
  const herePath = useRef(pathname);
  herePath.current = pathname;
  const setAskAbout = useCallback((entity: AskAboutEntity | null, opts?: { navigating?: boolean }) => {
    if (!entity) return setAbout(null);
    const here = herePath.current;
    const path = opts?.navigating ? new URL(entity.url, window.location.origin).pathname : here;
    setAbout({ entity, path, arrived: path === here, following: path !== here });
  }, []);
  // The pick's own navigation is the arrival wherever it lands, and the redirect that page makes at once
  // (a replace) carries the chip on to the final page. Any later page change drops it.
  const navigationType = useNavigationType();
  useEffect(() => {
    setAbout((prev) => {
      if (!prev || (prev.arrived && pathname === prev.path)) return prev;
      if (!prev.arrived || (prev.following && navigationType === "REPLACE")) {
        return { ...prev, path: pathname, arrived: true };
      }
      return null;
    });
  }, [pathname, navigationType]);
  // A redirect lands in the same turn as the arrival; after it, the chip stays on that page only.
  const following = Boolean(about?.arrived && about.following);
  useEffect(() => {
    if (!following) return;
    const settle = setTimeout(() => setAbout((prev) => prev && { ...prev, following: false }), 0);
    return () => clearTimeout(settle);
  }, [following, about?.path]);
  const askAbout = about && about.arrived && pathname === about.path ? about.entity : null;
  // A queue, not a slot: a second handoff while the session still loads never drops the first.
  const [paletteAsks, setPaletteAsks] = useState<PaletteAsk[]>([]);
  const askInTray = useCallback(
    (ask: { text: string | null; results: string | null }) =>
      setPaletteAsks((prev) => [...prev, { nonce: (prev.at(-1)?.nonce ?? 0) + 1, ...ask }].slice(-PALETTE_ASKS_KEPT)),
    [],
  );

  const [trayFocus, setTrayFocus] = useState<SharedTrayFocus | null>(null);
  const [picked, setPicked] = useState<TrayPick | null>(null);
  const pick = useCallback((key: string) => setPicked({ key, at: new Date() }), []);
  // In memory only: a pin lasts the tab, not the next visit.
  const [pinnedDrive, setPinnedDrive] = useState<{ id: string; label: string } | null>(null);
  const pinDrive = useCallback((drive: { id: string; label: string }) => setPinnedDrive(drive), []);
  const unpinDrive = useCallback(() => setPinnedDrive(null), []);
  const pageDriveId = driveSubjectId(subject);
  const pageDriveLabel = subject.label ?? "";
  // The page's own Drive wins; elsewhere the pinned one holds.
  const driveFocus = useMemo<DriveFocus | null>(() => {
    if (pageDriveId) {
      return { id: pageDriveId, label: pageDriveLabel, pinned: pinnedDrive?.id === pageDriveId };
    }
    return pinnedDrive ? { ...pinnedDrive, pinned: true } : null;
  }, [pageDriveId, pageDriveLabel, pinnedDrive]);

  const value = useMemo(
    () => ({
      subject,
      active,
      enabled,
      trayMode,
      setTrayMode,
      needsWidth,
      briefSlot,
      setBriefSlot,
      briefClaimed,
      claimBrief,
      claimOwnTray,
      openTray,
      openNonce,
      raiseTray,
      raiseNonce,
      setEnabled,
      declare,
      askAbout,
      setAskAbout,
      paletteAsks,
      askInTray,
      trayFocus,
      setTrayFocus,
      picked,
      pick,
      driveFocus,
      pinDrive,
      unpinDrive,
    }),
    [
      subject,
      active,
      enabled,
      trayMode,
      setTrayMode,
      needsWidth,
      briefSlot,
      briefClaimed,
      claimBrief,
      claimOwnTray,
      openTray,
      openNonce,
      raiseTray,
      raiseNonce,
      declare,
      askAbout,
      setAskAbout,
      paletteAsks,
      askInTray,
      trayFocus,
      picked,
      pick,
      driveFocus,
      pinDrive,
      unpinDrive,
    ],
  );
  return <KoryTrayContext.Provider value={value}>{children}</KoryTrayContext.Provider>;
}

/** Null outside the app shell (embeds, share links), where there is no tray. */
export function useKoryTray(): KoryTrayContextType | null {
  return useContext(KoryTrayContext) ?? null;
}

/** The Drive the tray talks to, with its pin; null outside the app shell or with no Drive in focus. */
export function useKoryDriveFocus(): Pick<KoryTrayContextType, "driveFocus" | "pinDrive" | "unpinDrive"> | null {
  const ctx = useContext(KoryTrayContext);
  if (!ctx) return null;
  return { driveFocus: ctx.driveFocus, pinDrive: ctx.pinDrive, unpinDrive: ctx.unpinDrive };
}

/**
 * Declare what this page is about for the global Kory tray. Pass null while
 * the subject is still loading. The declaration lasts while the caller is mounted.
 */
export function useKoryPageSubject(subject: KoryPageSubject | null): void {
  const ctx = useContext(KoryTrayContext);
  const declare = ctx?.declare;
  const key = useId();
  const serialized = subject ? JSON.stringify(subject) : null;
  const latest = useRef(subject);
  latest.current = subject;
  useEffect(() => {
    if (!declare) return;
    declare(key, latest.current);
    return () => declare(key, null);
  }, [declare, key, serialized]);
}

function useClaim(claim: ((key: string, on: boolean) => void) | undefined, on: boolean) {
  const key = useId();
  useEffect(() => {
    if (!claim || !on) return;
    claim(key, true);
    return () => claim(key, false);
  }, [claim, key, on]);
}

/**
 * A host page hands its rows to the global tray while ``on``: the tray renders this element where
 * its own rows go. Returns the element to portal into (null until the tray has mounted it, or while off).
 */
export function useKoryTrayBrief(on: boolean): HTMLElement | null {
  const ctx = useContext(KoryTrayContext);
  useClaim(ctx?.claimBrief, on);
  return on ? (ctx?.briefSlot ?? null) : null;
}

/** A host page mounted a tray of its own while ``on``: the global tray steps aside on it. */
export function useKoryTrayOwnTray(on: boolean): void {
  useClaim(useContext(KoryTrayContext)?.claimOwnTray, on);
}
